import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Seo from '../components/Seo';
import { useCart } from '../context/CartContext';
import { CATEGORY_SEO_MAP } from '../utils/categorySeoData';
import loadCategoryImages from '../utils/loadCategoriesImages';
import { generateWebsiteSchema, generateOrganizationSchema } from '../utils/schemaGenerators';

import smartCotiLogo from '../assets/logos/smart_coti-logo.svg';

import imgS1        from '../assets/slider/slider_1.webp';
import imgSublim    from '../assets/slider/sublimacion.png';
import imgS2        from '../assets/slider/slider_2.webp';
import imgMugs      from '../assets/slider/mugs.webp';
import imgS4        from '../assets/slider/slider_4.webp';
import imgTec       from '../assets/slider/tecnologia.jpg';
import imgEscritura from '../assets/slider/escritura.webp';
import imgMorrales  from '../assets/slider/morrales.jpg';

import '../styles/pages/HomeV2.scss';

const API_URL  = process.env.REACT_APP_API_URL;
const S3_URL   = process.env.REACT_APP_S3_BASE_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.promocionalesenstock.com';

const normalizeSlug = (s) => s.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
const displayName   = (cat) => CATEGORY_SEO_MAP[cat.slug]?.name || cat.name;

const STATS = [
  { value: '+8.000',  label: 'Productos' },
  { value: '+10.000', label: 'Clientes satisfechos' },
  { value: '5',       label: 'Proveedores mayoristas' },
  { value: '100%',    label: 'Personalizable' },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Explora el catálogo', desc: 'Navega entre +8.000 productos organizados por categoría.' },
  { step: '02', title: 'Agrega al carrito',   desc: 'Selecciona productos, variaciones y cantidades.' },
  { step: '03', title: 'Envía tu cotización', desc: 'Recibe respuesta de nuestro equipo en menos de 24 h.' },
];

const SHOWCASE = [
  { img: imgS1,        label: 'Personaliza tu marca',        sub: 'Con tu logo en cada producto',         to: '/productos/todos' },
  { img: imgSublim,    label: 'Sublimación',                  sub: 'Impresión de alta calidad y detalle',  to: '/categorias' },
  { img: imgS2,        label: '+8.000 productos para elegir', sub: 'La mayor variedad del mercado',        to: '/productos/todos' },
  { img: imgMugs,      label: 'Mugs y Vasos',                 sub: 'Personaliza hasta el último sorbo',    to: '/categorias' },
  { img: imgS4,        label: 'Entrega rápida y segura',      sub: 'A todo el país, directo a tu puerta',  to: '/categorias' },
  { img: imgTec,       label: 'Tecnología',                   sub: 'Gadgets y accesorios corporativos',    to: '/categorias' },
  { img: imgEscritura, label: 'Escritura',                    sub: 'Bolígrafos, marcadores y más',         to: '/categorias' },
  { img: imgMorrales,  label: 'Morrales y Bolsos',            sub: 'Tu marca en cada viaje',               to: '/categorias' },
];

const HomeV2 = () => {
  const navigate = useNavigate();
  const { openSmartCoti } = useCart();
  const [featuredCats,  setFeaturedCats]  = useState([]);
  const [featuredProds, setFeaturedProds] = useState([]);
  const [newProds,      setNewProds]      = useState([]);
  const categoryImages = loadCategoryImages();

  // ── Búsqueda en vivo ──────────────────────────────────────
  const [searchQ,       setSearchQ]       = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [searching,     setSearching]     = useState(false);
  const searchDebounce = useRef(null);
  const searchWrapRef  = useRef(null);

  useEffect(() => {
    const q = searchQ.trim();
    clearTimeout(searchDebounce.current);
    if (!q) { setSearchResults([]); setSearchOpen(false); return; }
    searchDebounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/products`, {
          headers: { authorization: token },
          params:  { search: q, limit: 6 },
        });
        const list = Array.isArray(data) ? data : (data.products ?? []);
        setSearchResults(list.slice(0, 6));
        setSearchOpen(list.length > 0);
      } catch { setSearchResults([]); }
      finally  { setSearching(false); }
    }, 320);
  }, [searchQ]);

  useEffect(() => {
    const handler = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target))
        setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goSearch = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    if (!q) return;
    setSearchOpen(false);
    navigate(`/productos/todos?search=${encodeURIComponent(q)}`);
  };

  // ── Hero mosaic — pool rotatorio ──────────────────────────
  const [prodPool,    setProdPool]    = useState([]);
  const [heroIndices, setHeroIndices] = useState([0, 1, 2, 3]);
  const [fadingSlot,  setFadingSlot]  = useState(null);
  const rotateRef = useRef({ slot: 0, poolIdx: 4 });

  // ── Showcase slider ───────────────────────────────────────
  const [slide,       setSlide]       = useState(0);
  const [paused,      setPaused]      = useState(false);
  const timerRef = useRef(null);

  const goTo = (idx) => setSlide((idx + SHOWCASE.length) % SHOWCASE.length);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => setSlide(s => (s + 1) % SHOWCASE.length), 4500);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  // ── Categorías ────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/categories`, {
          headers: { authorization: token },
        });
        const sorted = data
          .sort((a, b) => displayName(a).localeCompare(displayName(b), 'es'))
          .slice(0, 8);
        setFeaturedCats(sorted);
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  // ── Productos destacados + nuevos ─────────────────────────
  useEffect(() => {
    const loadProds = async (endpoint, setter) => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/products/${endpoint}`, {
          headers: { authorization: token },
        });
        setter(Array.isArray(data) ? data.slice(0, 10) : []);
      } catch (e) { /* silent */ }
    };
    loadProds('deals',          setFeaturedProds);
    loadProds('random-products', setNewProds);
  }, []);

  // ── Pool del hero (random, para rotación) ─────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/products/random-products`, {
          headers: { authorization: token },
        });
        if (Array.isArray(data) && data.length > 4) {
          setProdPool(data);
          setHeroIndices([0, 1, 2, 3]);
          rotateRef.current = { slot: 0, poolIdx: 4 };
        }
      } catch (e) { /* silent */ }
    };
    load();
  }, []);

  // ── Rotación del mosaico ──────────────────────────────────
  useEffect(() => {
    if (prodPool.length < 5) return;
    const id = setInterval(() => {
      const { slot, poolIdx } = rotateRef.current;
      setFadingSlot(slot);
      setTimeout(() => {
        setHeroIndices((prev) => {
          const next = [...prev];
          next[slot] = poolIdx % prodPool.length;
          return next;
        });
        setFadingSlot(null);
        rotateRef.current = {
          slot:    (slot + 1) % 4,
          poolIdx: (poolIdx + 1) % prodPool.length,
        };
      }, 380);
    }, 3200);
    return () => clearInterval(id);
  }, [prodPool.length]);

  const thumbUrl = (product) =>
    product?.image_files?.length
      ? `${S3_URL}/thumbnails/${product.image_files[0]}`
      : null;

  const schemaOrg = generateOrganizationSchema?.() || null;
  const schemaWeb = generateWebsiteSchema?.() || null;

  return (
    <div className="HV2">
      <Seo
        title="Promocionales en Stock — Personaliza tu marca"
        description="Catálogo de productos promocionales personalizados para empresas en Colombia. +8.000 artículos con tu logo."
        canonicalUrl="/"
        ogType="website"
        jsonLd={schemaOrg || schemaWeb}
        siteUrl={SITE_URL}
      />

      {/* ── HERO ────────────────────────────────────────────── */}
      <section className="HV2__hero">
        <div className="HV2__hero-inner container">

          {/* Columna izquierda — texto */}
          <div className="HV2__hero-text">

            {/* ── Buscador ── */}
            <form className="HV2__search" onSubmit={goSearch} ref={searchWrapRef}>
              <div className="HV2__search-bar">
                <svg className="HV2__search-ico" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  className="HV2__search-input"
                  placeholder="Buscar productos..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  autoComplete="off"
                />
                {searching && <span className="HV2__search-spinner" />}
                {searchQ && !searching && (
                  <button type="button" className="HV2__search-clear" onClick={() => { setSearchQ(''); setSearchOpen(false); }}>✕</button>
                )}
                <button type="submit" className="HV2__search-btn">Buscar</button>
              </div>

              {searchOpen && searchResults.length > 0 && (
                <ul className="HV2__search-drop">
                  {searchResults.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/productos/${p.slug}`}
                        className="HV2__search-item"
                        onClick={() => setSearchOpen(false)}
                      >
                        {thumbUrl(p)
                          ? <img src={thumbUrl(p)} alt={p.name} className="HV2__search-thumb" />
                          : <span className="HV2__search-thumb HV2__search-thumb--empty" />
                        }
                        <span className="HV2__search-name">{p.name}</span>
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button type="submit" className="HV2__search-all">
                      Ver todos los resultados para "{searchQ}" →
                    </button>
                  </li>
                </ul>
              )}
            </form>

            <span className="HV2__hero-badge">Colombia · B2B</span>
            <h1 className="HV2__hero-title">
              Productos promocionales<br />
              <em>que hablan por tu marca.</em>
            </h1>
            <p className="HV2__hero-sub">
              Personaliza +8.000 artículos con el logo de tu empresa.
              Cotización rápida, entrega a todo el país.
            </p>

            <div className="HV2__hero-actions">
              <Link to="/categorias" className="HV2__btn HV2__btn--primary">
                Ver catálogo
              </Link>
              <button className="HV2__btn HV2__btn--outline" onClick={openSmartCoti}>
                <img src={smartCotiLogo} alt="Smart Coti" className="HV2__btn-icon" />
                Cotización inteligente
              </button>
            </div>
            <div className="HV2__hero-stats">
              {STATS.map((s) => (
                <div key={s.label} className="HV2__stat">
                  <span className="HV2__stat-value">{s.value}</span>
                  <span className="HV2__stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Columna derecha — mosaico rotatorio */}
          <div className="HV2__hero-visual" aria-hidden="true">
            <div className="HV2__hero-mosaic">
              {(prodPool.length > 0
                ? heroIndices.map((idx) => prodPool[idx])
                : Array(4).fill(null)
              ).map((p, slot) =>
                p ? (
                  <Link
                    key={`${slot}-${p.id}`}
                    to={`/productos/${p.slug}`}
                    className={`HV2__hero-card HV2__hero-card--${slot}${fadingSlot === slot ? ' HV2__hero-card--fading' : ''}`}
                  >
                    {thumbUrl(p)
                      ? <img src={thumbUrl(p)} alt={p.name} loading="lazy" />
                      : <span className="HV2__hero-card-placeholder" />
                    }
                    <p className="HV2__hero-card-name">{p.name}</p>
                  </Link>
                ) : (
                  <div key={slot} className={`HV2__hero-card HV2__hero-card--${slot} HV2__hero-card--skel`} />
                )
              )}
            </div>
          </div>

        </div>

        <div className="HV2__hero-scroll-hint"><span /></div>
      </section>

      {/* ── SHOWCASE SLIDER ─────────────────────────────────── */}
      <section
        className="HV2__showcase"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Track */}
        <div
          className="HV2__showcase-track"
          style={{ transform: `translateX(-${slide * 100}%)` }}
        >
          {SHOWCASE.map((s, i) => (
            <div key={i} className="HV2__showcase-slide">
              <img src={s.img} alt={s.label} />
              <div className="HV2__showcase-overlay">
                <p className="HV2__showcase-label">{s.label}</p>
                <p className="HV2__showcase-sub">{s.sub}</p>
                <Link to={s.to} className="HV2__showcase-btn">
                  Ver productos →
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Flechas */}
        <button
          className="HV2__showcase-arrow HV2__showcase-arrow--l"
          onClick={() => goTo(slide - 1)}
          aria-label="Anterior"
        >‹</button>
        <button
          className="HV2__showcase-arrow HV2__showcase-arrow--r"
          onClick={() => goTo(slide + 1)}
          aria-label="Siguiente"
        >›</button>

        {/* Dots */}
        <div className="HV2__showcase-dots">
          {SHOWCASE.map((_, i) => (
            <button
              key={i}
              className={`HV2__showcase-dot${slide === i ? ' HV2__showcase-dot--active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ── CATEGORÍAS ──────────────────────────────────────── */}
      <section className="HV2__section container">
        <div className="HV2__section-head">
          <h2 className="HV2__section-title">Explora por categoría</h2>
          <Link to="/categorias" className="HV2__section-link">Ver todas →</Link>
        </div>

        <div className="HV2__cats-grid">
          {featuredCats.length === 0
            ? [...Array(8)].map((_, i) => <div key={i} className="HV2__skeleton HV2__skeleton--cat" />)
            : featuredCats.map((cat) => {
                const imgKey = `${normalizeSlug(cat.slug)}.svg`;
                const img    = categoryImages[imgKey];
                const name   = displayName(cat);
                return (
                  <Link key={cat.id} to={`/categorias/${cat.slug}`} className="HV2__cat-card">
                    <div className="HV2__cat-icon-wrap">
                      {img
                        ? <img src={img.default || img} alt={name} loading="lazy" />
                        : <span>📦</span>
                      }
                    </div>
                    <span className="HV2__cat-name">{name}</span>
                  </Link>
                );
              })
          }
        </div>
      </section>

      {/* ── OFERTAS ─────────────────────────────────────────── */}
      {featuredProds.length > 0 && (
        <section className="HV2__deals-section" id="deals">

          {/* Banner hero */}
          <div className="HV2__deals-banner">
            <div className="HV2__deals-banner-inner container">
              <div className="HV2__deals-banner-left">
                <span className="HV2__deals-flame" aria-hidden="true">🔥</span>
                <div>
                  <p className="HV2__deals-eyebrow">Promociones exclusivas</p>
                  <h2 className="HV2__deals-banner-title">Ofertas especiales</h2>
                  <p className="HV2__deals-banner-sub">Favoritos imperdibles · Existencias limitadas</p>
                </div>
              </div>
              <Link to="/productos/todos" className="HV2__deals-banner-cta">
                Ver todos los productos →
              </Link>
            </div>
          </div>

          {/* Carrusel */}
          <div className="HV2__deals-carousel container">
            <div className="HV2__deals-track">
              {featuredProds.map((p) => {
                const thumb = thumbUrl(p);
                return (
                  <Link key={p.id || p.slug} to={`/productos/${p.slug}`} className="HV2__deal-card">
                    <div className="HV2__deal-img-wrap">
                      <span className="HV2__deal-badge">OFERTA</span>
                      {thumb
                        ? <img src={thumb} alt={p.title} loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        : <div className="HV2__prod-img-placeholder">📷</div>
                      }
                    </div>
                    <div className="HV2__deal-info">
                      {p.inventoryTotal > 0 && (
                        <span className="HV2__deal-stock">
                          En Stock: {p.inventoryTotal.toLocaleString('es-CO')}
                        </span>
                      )}
                      <p className="HV2__deal-title">{p.title}</p>
                    </div>
                    <span className="HV2__deal-cta">Cotizar</span>
                  </Link>
                );
              })}
            </div>
          </div>

        </section>
      )}

      {/* ── NUEVOS PRODUCTOS ────────────────────────────────── */}
      {newProds.length > 0 && (
        <section className="HV2__section container">
          <div className="HV2__section-head">
            <h2 className="HV2__section-title">Novedades</h2>
            <Link to="/productos/todos" className="HV2__section-link">Ver todos →</Link>
          </div>
          <div className="HV2__prods-grid">
            {newProds.map((p) => {
              const thumb = thumbUrl(p);
              return (
                <Link key={p.id || p.slug} to={`/productos/${p.slug}`} className="HV2__prod-card">
                  <div className="HV2__prod-img-wrap">
                    <span className="HV2__prod-badge">Nuevo</span>
                    {thumb
                      ? <img src={thumb} alt={p.title} loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      : <div className="HV2__prod-img-placeholder">📷</div>
                    }
                  </div>
                  <div className="HV2__prod-info">
                    <p className="HV2__prod-title">{p.title}</p>
                  </div>
                  <span className="HV2__prod-cta">Cotizar →</span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ── CÓMO FUNCIONA ───────────────────────────────────── */}
      <section className="HV2__how HV2__section--dark">
        <div className="container">
          <h2 className="HV2__section-title HV2__section-title--light">¿Cómo funciona?</h2>
          <div className="HV2__how-grid">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="HV2__how-card">
                <span className="HV2__how-step">{item.step}</span>
                <h3 className="HV2__how-title">{item.title}</h3>
                <p className="HV2__how-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SMART COTI CTA ──────────────────────────────────── */}
      <section className="HV2__smart-coti container">
        <div className="HV2__smart-inner">
          <div className="HV2__smart-text">
            <h2 className="HV2__smart-title">¿No encuentras lo que buscas?</h2>
            <p className="HV2__smart-sub">
              Usa nuestra cotización inteligente. Dinos qué necesitas y
              te buscamos el producto ideal al mejor precio.
            </p>
            <button className="HV2__btn HV2__btn--primary" onClick={openSmartCoti}>
              Iniciar cotización inteligente
            </button>
          </div>
          <img src={smartCotiLogo} alt="Smart Coti" className="HV2__smart-img" />
        </div>
      </section>
    </div>
  );
};

export default HomeV2;
